/**
 * Detail view of a campaign — chapters, activities and the auto-created
 * DRAFT WorldSessions. The admin can edit each chapter's start/end dates
 * and schedule (or reschedule) gameplay sessions inline without leaving
 * this screen.
 */

import { formatDate, formatDateTime } from '../../../utils/format-date';
import { instantFromInputs, localDateInput, localTimeInput } from '../../../utils/date-input';
import { saveFailure, loadFailure, type FormFailure } from '../save-failure-words';
import { Input, FormMessage } from '../../../components/form-field';
import { extractSkillRewards } from '../../../api/_shared/activity-payload';
import { Table } from '../../../components/table';
import { CampaignStatusName } from './campaign-words';
import { ActivityKindPill } from '../../shell/activity-kind-pill';
import { getSessionStatus, sessionNow, type SessionStatusView } from '../session-status';
import { SessionStatusPill } from '../session-status-pill';
import { useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../../shell/common-header';
import type { AdminCampaignChapter, AdminCampaignActivity } from '../../../api/admin/campaigns/types';
import { useAdminSessions, useInterruptedSessionIds, useUpdateSession } from '../../../api/admin/sessions/hooks';
import { useAdminCampaign, useUpdateCampaignChapter } from '../../../api/admin/campaigns/hooks';
import admin from '../admin-dashboard.module.css';

interface Props {
  id: string;
  onBack: () => void;
}

export function CampaignDetail({ id, onBack }: Props) {
  const { t } = useTranslation();
  const query = useAdminCampaign(id);
  const data = query.data ?? null;
  const loading = query.isLoading;
  const error = query.isError ? loadFailure(t, query.error, 'campaign') : null;

  // Mutations elsewhere on this page (chapter editor, session scheduler) call
  // `reload()` after they succeed. Hooks-based mutations invalidate the cache
  // themselves, but child components still use the imperative reload pattern
  // — point it at the query's refetch for backward compatibility.
  const reload = () => { void query.refetch(); };

  // A gameplay activity's session runs on the campaign's world, and stands as
  // the world's sessions have it now (sessionNow): the check reads them again
  // while one runs. One running whose world the game server runs no match on
  // is interrupted, as the sessions screen says it (useInterruptedSessionIds).
  const worldSessions = useAdminSessions(data?.gameName ?? null, data?.worldName ?? null).data;
  const anyRunning = (data?.chapters ?? []).some((ch) =>
    ch.activities.some((a) => a.worldSession !== null && sessionNow(a.worldSession, worldSessions).status === 'RUNNING'),
  );
  const interruptedSessionIds = useInterruptedSessionIds(data?.gameName ?? null, data?.worldName ?? null, anyRunning);

  if (loading) {
    return (
      <div className="portal-shell">
        <CommonHeader title={t('admin.campaigns.detail.title')} showBackButton onBackClick={onBack} backLabel={t('common.backToList')} />
        <main className={admin.adminPanelMain}><div className={admin.adminLoading}>{t('common.loading')}</div></main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="portal-shell">
        <CommonHeader title={t('admin.campaigns.detail.title')} showBackButton onBackClick={onBack} backLabel={t('common.backToList')} />
        <main className={admin.adminPanelMain}><FormMessage title={error?.title ?? t('requestErrors.notFound')}>{error?.detail}</FormMessage></main>
      </div>
    );
  }

  return (
    <div className="portal-shell">
      <CommonHeader title={data.name} showBackButton onBackClick={onBack} backLabel={t('common.backToList')} />

      <main className={admin.adminPanelMain}>
        <div className={admin.adminTableWrapper}>
          <div className="p-4">
            <p><strong>{t('admin.campaigns.form.template')}:</strong> {data.templateKey}</p>
            <p><strong>{t('admin.campaigns.form.game')}:</strong> {data.gameName}</p>
            <p><strong>{t('admin.campaigns.columns.status')}:</strong> <CampaignStatusName status={data.status} /></p>
            <p>
              <strong>{t('admin.campaigns.columns.dates')}:</strong>{' '}
              {data.startDateUtc ? formatDate(data.startDateUtc) : '—'}
              {' → '}
              {data.endDateUtc ? formatDate(data.endDateUtc) : '—'}
            </p>
            <p><strong>{t('admin.campaigns.columns.cohorts')}:</strong> {data.cohorts.map((c) => c.name).join(', ') || '—'}</p>
            <p><strong>{t('admin.campaigns.detail.world')}:</strong> {data.worldName}</p>
          </div>

          {data.chapters.map((ch) => (
            <div key={ch.id} className={`${admin.adminChapterRow} p-4`}>
              <h3 className="m-0 mb-2">{ch.title}</h3>
              {ch.description && <p className={`${admin.adminChapterDesc} tone-soft`}>{ch.description}</p>}

              <ChapterDateEditor
                campaignId={data.id}
                chapter={ch}
                campaignStart={data.startDateUtc}
                campaignEnd={data.endDateUtc}
                onUpdated={reload}
              />

              <Table flush className={admin.adminTableSpaced}>
                <thead>
                  <tr>
                    <th>{t('admin.campaigns.detail.activity')}</th>
                    <th>{t('admin.campaigns.detail.type')}</th>
                    <th>{t('admin.campaigns.detail.xp')}</th>
                    <th>{t('admin.campaigns.detail.kp')}</th>
                    <th>{t('admin.campaigns.detail.skills')}</th>
                    <th>{t('admin.campaigns.detail.session')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ch.activities.map((a) => {
                    const skills = extractSkillRewards(a.payload);
                    return (
                      <tr key={a.id}>
                        <td>{a.title} <code className="tone-dim">({a.activityKey})</code></td>
                        <td>
                          <ActivityKindPill kind={a.type} />
                        </td>
                        <td>{a.xpReward}</td>
                        <td>{a.kpReward}</td>
                        <td>
                          {skills.length === 0
                            ? '—'
                            : skills
                                .map((s) => `${t(`skills.${s.skillId}.name`, { defaultValue: s.skillId })} +${s.points}`)
                                .join(', ')}
                        </td>
                        <td>
                          {a.type === 'GAMEPLAY' && a.worldSession ? (
                            <SessionScheduler
                              campaignId={data.id}
                              gameName={data.gameName}
                              worldName={data.worldName}
                              cohortNames={data.cohorts.map((c) => c.name)}
                              activity={a}
                              campaignEnd={data.endDateUtc}
                              status={getSessionStatus(sessionNow(a.worldSession, worldSessions), interruptedSessionIds.has(a.worldSession.id))}
                              onUpdated={reload}
                            />
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

// ── Chapter date editor (start only — end is determined by campaign end date) ──

interface ChapterDateEditorProps {
  campaignId: string;
  chapter: AdminCampaignChapter;
  campaignStart: number | null;
  campaignEnd: number | null;
  onUpdated: () => void;
}

function ChapterDateEditor({ campaignId, chapter, campaignStart, campaignEnd, onUpdated }: ChapterDateEditorProps) {
  const { t } = useTranslation();
  // The chapter's local day, as its input holds it and its save reads it.
  const initialStart = chapter.startDateUtc ? localDateInput(chapter.startDateUtc) : '';
  const [start, setStart] = useState(initialStart);
  const updateChapter = useUpdateCampaignChapter();
  const saving = updateChapter.isPending;
  const [error, setError] = useState<FormFailure | null>(null);

  const minDate = campaignStart ? localDateInput(campaignStart) : undefined;
  const maxDate = campaignEnd ? localDateInput(campaignEnd) : undefined;

  const dirty = start !== initialStart;

  async function save() {
    setError(null);
    try {
      const startIso = start ? new Date(`${start}T00:00:00`).toISOString() : null;
      await updateChapter.mutateAsync({
        campaignId,
        chapterId: chapter.id,
        payload: { startTime: startIso },
      });
      onUpdated();
    } catch (e) {
      setError(saveFailure(t, e, 'chapterDate'));
    }
  }

  return (
    <div className="flex items-center gap-2 mb-3 text-12 flex-wrap">
      <strong>{t('admin.campaigns.detail.chapterStart')}:</strong>
      <Input
        type="date"
        className="max-w-[180px]"
        value={start}
        min={minDate}
        max={maxDate}
        onChange={(e) => setStart(e.target.value)}
      />
      <Button type="button" variant="ghost" size="sm" disabled={saving || !dirty} onClick={() => void save()}
      >
        {saving ? t('common.loading') : t('common.save')}
      </Button>
      {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
    </div>
  );
}

// ── Inline session scheduler for a gameplay activity ──────────────────────

interface SessionSchedulerProps {
  campaignId: string;
  gameName: string;
  worldName: string;
  cohortNames: string[];
  activity: AdminCampaignActivity;
  /** The campaign's end: no session of it may start after it, so its day is the last the date can take. */
  campaignEnd: number | null;
  /** Where its session stands now: the world's sessions' status, and interrupted with no match on its world. */
  status: SessionStatusView;
  onUpdated: () => void;
}

function SessionScheduler({ gameName, worldName, cohortNames, activity, campaignEnd, status, onUpdated }: SessionSchedulerProps) {
  const { t } = useTranslation();
  const session = activity.worldSession!;

  // The session's local day and time, as the inputs hold them and the save reads them.
  const [date, setDate] = useState(session.startTimeUtc ? localDateInput(session.startTimeUtc) : '');
  const [time, setTime] = useState(session.startTimeUtc ? localTimeInput(session.startTimeUtc) : '09:00');
  const [duration, setDuration] = useState<number>(session.durationMinutes ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FormFailure | null>(null);
  const [editing, setEditing] = useState(false);
  const updateSession = useUpdateSession();

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const startTimeUtc = instantFromInputs(date, time);
      await updateSession.mutateAsync({
        gameName,
        worldName,
        externalId: session.externalId,
        startTimeUtc,
        durationMinutes: duration,
        cohortIds: cohortNames,
      });
      setEditing(false);
      onUpdated();
    } catch (e) {
      // A session that would start after its campaign's end is refused, and said in the admin's words.
      setError(saveFailure(t, e, 'session'));
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <SessionStatusPill status={status} />
        <span>
          {session.startTimeUtc
            ? formatDateTime(session.startTimeUtc)
            : t('admin.campaigns.detail.notScheduled')}
          {session.durationMinutes ? ` · ${session.durationMinutes} min` : ''}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}
        >
          {t('admin.campaigns.detail.scheduleSession')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Input
        type="date"
        className="max-w-[160px]"
        max={campaignEnd ? localDateInput(campaignEnd) : undefined}
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <Input
        type="time"
        className="max-w-[110px]"
        value={time}
        onChange={(e) => setTime(e.target.value)}
      />
      <Input
        type="number"
        className="max-w-[90px]"
        min={0}
        value={duration}
        onChange={(e) => setDuration(Number(e.target.value))}
        title={t('admin.campaigns.detail.durationMinutes')}
      />
      <span className={`${admin.adminDurationHint} tone-soft`}>{t('admin.campaigns.detail.durationHint')}</span>
      <Button type="button" size="sm" disabled={saving || !date || !time} onClick={() => void save()}
      >
        {saving ? t('common.loading') : t('common.save')}
      </Button>
      <Button type="button" variant="ghost" size="sm" disabled={saving} onClick={() => { setEditing(false); setError(null); }}
      >
        {t('common.cancel')}
      </Button>
      {error && <FormMessage className={admin.adminFormErrorInline} title={error.title}>{error.detail}</FormMessage>}
    </div>
  );
}
